import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import {
  invalidateCustomerDocumentCategoryCache,
  useGetCustomerDocumentCategory,
  useUpdateCustomerDocumentCategory,
} from 'src/api/customer-document-category'
import { GenericDialogLayout } from 'src/components/GenericDialogLayout'
import { ShowDialogProps } from 'src/show-dialog'
import { DocumentCategoryForm } from 'src/screens/customer/document-categories/DocumentCategoryForm'
import { IDocumentCategory } from 'src/generated-types/document-category'
import { DialogSpinner } from 'src/components/Spinner'
import { toast } from 'react-toastify'

interface UpdateCustomerDocumentCategoryProps extends ShowDialogProps {
  id: EntityId
  customerId: EntityId
}

export const UpdateCustomerDocumentCategoryDialog: React.FC<UpdateCustomerDocumentCategoryProps> = ({
  pop,
  id,
  customerId,
}) => {
  const { t: translate } = useTranslation(['documentcategory', 'common', 'common-api-message'])
  const { data, isLoading } = useGetCustomerDocumentCategory(customerId, id)
  const mutation = useUpdateCustomerDocumentCategory(customerId, id as EntityId)

  const onSubmit = useCallback(
    async form => {
      await mutation.mutateAsync(form, {
        onSuccess: () => {
          toast.success(translate('successfullyUpdated', { ns: 'common-api-message' }))
          invalidateCustomerDocumentCategoryCache.getCustomerDocumentCategories(customerId)
          invalidateCustomerDocumentCategoryCache.getCustomerDocumentCategory(customerId, id)
          invalidateCustomerDocumentCategoryCache.getCustomerAndAdminDocumentCategories(customerId)
          pop(true)
        },
        onError: error => {
          if (error && error['data'] === 'DocumentCategoryAlreadyExists') {
            toast.warning(translate('documentCategoryAlreadyExists', { ns: 'document-category' }))
          } else {
            toast.error(translate('cannotUpdateSystemGeneratedDocumentCategory', { ns: 'document-category' }))
          }
        },
      })
    },
    [mutation, translate, customerId, id, pop],
  )

  if (isLoading) return <DialogSpinner />

  return (
    <GenericDialogLayout title={translate('updateDocumentCategory', { ns: 'document-category' })} pop={pop}>
      <DocumentCategoryForm
        onSubmit={onSubmit}
        onCancel={() => pop()}
        initialValues={data as IDocumentCategory}
        title={''}
      />
    </GenericDialogLayout>
  )
}
